const DAY_PART_WINDOWS = {
  morning: [5 * 60, 12 * 60],
  breakfast: [5 * 60, 11 * 60],
  lunch: [11 * 60, 14 * 60],
  afternoon: [12 * 60, 17 * 60],
  dinner: [16 * 60, 20 * 60],
  evening: [17 * 60, 22 * 60],
  bedtime: [19 * 60, 24 * 60],
};

const normalizeText = (value = '') => String(value || '').trim();

export const MEDICATION_ADMINISTRATION_OUTCOMES = [
  { value: 'taken', label: 'Taken' },
  { value: 'refused', label: 'Refused' },
  { value: 'partial', label: 'Partly taken' },
  { value: 'late', label: 'Given late' },
  { value: 'missed', label: 'Missed' },
];

const parseClockMinutes = (value = '') => {
  const text = normalizeText(value).toLowerCase();
  if (!text) return null;

  const meridiem = text.endsWith('am') ? 'am' : text.endsWith('pm') ? 'pm' : '';
  const clock = meridiem ? text.slice(0, -2).trim() : text;
  const [hourText, minuteText = '0'] = clock.split(':');
  let hours = Number(hourText);
  const minutes = Number(minuteText);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || minutes < 0 || minutes > 59) return null;
  if (meridiem === 'pm' && hours < 12) hours += 12;
  if (meridiem === 'am' && hours === 12) hours = 0;
  return hours >= 0 && hours <= 23 ? (hours * 60) + minutes : null;
};

const getSchedules = (medication = {}) => (
  Array.isArray(medication.schedules) && medication.schedules.length
    ? medication.schedules
    : [{
        id: `${medication.id || 'medication'}-schedule`,
        time: medication.time || '',
        dose: medication.dose || '',
        unit: medication.unit || 'mg',
      }]
);

const scheduleMatchesReviewTime = (schedule = {}, review = {}) => {
  const scheduleMinutes = parseClockMinutes(schedule.time);
  if (scheduleMinutes === null) return false;

  const resolvedMinutes = parseClockMinutes(review.localTime);
  if (resolvedMinutes !== null) {
    return Math.abs(scheduleMinutes - resolvedMinutes) <= 120;
  }

  const dayPart = normalizeText(review.timeHint).toLowerCase();
  const window = DAY_PART_WINDOWS[dayPart];
  return Boolean(window && scheduleMinutes >= window[0] && scheduleMinutes < window[1]);
};

export const buildMedicationAdministrationOptions = (medications = [], review = {}) => (
  (Array.isArray(medications) ? medications : [])
    .filter((medication) => medication && !medication.isArchived)
    .map((medication) => {
      const schedules = getSchedules(medication);
      const matchingSchedule = schedules.find((schedule) => scheduleMatchesReviewTime(schedule, review)) || null;
      return {
        id: medication.id,
        medication,
        schedule: matchingSchedule || schedules[0] || null,
        scheduledForDetectedTime: Boolean(matchingSchedule),
      };
    })
);

export const getAdministrationOptionLabel = (option = {}) => {
  const medication = option.medication || {};
  const schedule = option.schedule || {};
  const dose = [schedule.dose || medication.dose, schedule.unit || medication.unit]
    .filter(Boolean)
    .join(' ');
  return [medication.name || 'Medication', dose].filter(Boolean).join(' — ');
};

export const getInitialMedicationAdministrationSelection = (options = [], review = {}) => {
  const medicationId = normalizeText(review.medicationId);
  if (medicationId && options.some((option) => option.id === medicationId)) {
    return [medicationId];
  }

  const medicationName = normalizeText(review.medicationName).toLowerCase();
  if (!medicationName) return [];

  const exactMatches = options.filter((option) => (
    normalizeText(option.medication?.name).toLowerCase() === medicationName
  ));
  return exactMatches.length === 1 ? [exactMatches[0].id] : [];
};

export const buildConfirmedMedicationAdministrations = (options = [], selectedIds = [], administrationStatus = 'taken') => {
  const selected = new Set(selectedIds);
  const allowedStatuses = ['taken', 'missed', 'late', 'refused', 'partial', 'extra', 'vomited', 'uncertain'];
  const normalizedStatus = allowedStatuses.includes(administrationStatus) ? administrationStatus : 'uncertain';
  return options
    .filter((option) => selected.has(option.id))
    .map((option) => ({
      medicationId: option.medication.id,
      medicationName: option.medication.name || '',
      scheduleId: option.schedule?.id || null,
      dose: option.schedule?.dose || option.medication.dose || '',
      unit: option.schedule?.unit || option.medication.unit || '',
      scheduledTime: option.schedule?.time || option.medication.time || null,
      status: normalizedStatus,
    }));
};

export const getMedicationAdministrationReviewCopy = (review = {}) => {
  const status = normalizeText(review.administrationStatus).toLowerCase() || 'uncertain';
  const copyByStatus = {
    taken: ['given', 'Confirm medication given', 'Medication administration confirmed'],
    missed: ['missed', 'Confirm medication missed', 'Missed medication confirmed'],
    late: ['given late', 'Confirm medication given late', 'Late medication confirmed'],
    refused: ['refused', 'Confirm medication refused', 'Medication refusal confirmed'],
    partial: ['partly given', 'Confirm partial dose', 'Partial medication dose confirmed'],
    extra: ['given as an extra dose', 'Confirm extra dose', 'Extra medication dose confirmed'],
    vomited: ['vomited', 'Confirm medication was vomited', 'Vomited medication confirmed'],
    uncertain: ['mentioned', 'Review medication', 'Medication details confirmed'],
  };
  const [titleVerb, actionLabel, confirmedLabel] = copyByStatus[status] || copyByStatus.uncertain;
  const isTaken = status === 'taken';
  return {
    administrationStatus: status,
    titleVerb,
    actionLabel,
    confirmedLabel,
    instruction: `Select every medication that was ${titleVerb}.`,
    dismissPrompt: `Keep this as an unconfirmed note and stop asking which medication was ${isTaken ? 'given' : titleVerb}?`,
  };
};

export const getMedicationAdministrationConfirmationQuestion = ({ review = {}, option = {}, status = '' } = {}) => {
  const medication = option.medication || {};
  const schedule = option.schedule || {};
  const medicationName = normalizeText(medication.name || review.medicationName) || 'this medication';
  const dose = [schedule.dose || medication.dose, schedule.unit || medication.unit].filter(Boolean).join(' ');
  const time = normalizeText(schedule.time || review.localTime);
  const timeLabel = time ? new Date(`2000-01-01T${time.length === 5 ? `${time}:00` : time}`).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  }) : '';
  const outcome = normalizeText(status || review.administrationStatus).toLowerCase();
  const outcomeText = {
    taken: 'was taken',
    refused: 'was not taken',
    partial: 'was partly taken',
    late: 'was taken late',
    missed: 'was missed',
  }[outcome] || 'has the selected final outcome';
  return `Confirm that ${[medicationName, dose].filter(Boolean).join(' ')}${timeLabel ? ` scheduled for ${timeLabel}` : ''} ${outcomeText}.`;
};

export const formatAdministrationTimeLabel = (review = {}) => (
  normalizeText(review.timeHint || review.localTime) || 'Time not specified'
);

export { parseClockMinutes };
